/**
 * Profile data — shared Zod validation schema.
 *
 * Field rules mirror BACKEND_CONTRACT.md `/user/signup` and
 * `/user/profile` exactly. Both endpoints accept the same shape;
 * signup is POST-only and profile updates use PATCH semantics, but
 * the per-field rules are identical, so a single schema covers both.
 *
 * Used by:
 *   - SignupPage  (full schema validated on Submit; per-step via trigger)
 *   - ProfilePage (full schema validated on Save)
 *
 * `age` quirk:
 *   The form input is a TEXT field, so its native value is a string.
 *   We accept the string, normalize Persian/Arabic digits, then pipe
 *   into a numeric schema. Final parsed `age` is a JS number — that's
 *   what the backend expects. ProfilePage pre-fills from user.age which
 *   is a number on disk; it must be converted with String() before
 *   feeding into the form's defaultValues.
 */

import { z } from 'zod';
import { normalizeDigits } from '@/utils/digits';

/**
 * Build the Zod schema with translated error messages.
 *
 * @param {(key: string) => string} t  i18next `t` function
 * @returns {z.ZodType}
 */
export function buildProfileSchema(t) {
  // Trim entries, reject empty strings (the Autocomplete can produce
  // whitespace-only entries if the user hits Enter on a blank input).
  // Then dedupe — `Array.from(new Set(...))` preserves order.
  const stringArray = z
    .array(z.string().trim().min(1))
    .transform((arr) => Array.from(new Set(arr)));

  return z.object({
    firstName: z
      .string()
      .trim()
      .min(2, t('signup.validation.firstNameTooShort'))
      .max(50, t('signup.validation.firstNameTooLong')),
    lastName: z
      .string()
      .trim()
      .min(2, t('signup.validation.lastNameTooShort'))
      .max(50, t('signup.validation.lastNameTooLong')),
    age: z
      .string()
      .trim()
      .min(1, t('signup.validation.ageRequired'))
      .transform(normalizeDigits)
      .pipe(
        z.coerce
          .number({ invalid_type_error: t('signup.validation.ageInvalid') })
          .int(t('signup.validation.ageInvalid'))
          .min(1, t('signup.validation.ageTooLow'))
          .max(120, t('signup.validation.ageTooHigh'))
      ),
    gender: z.enum(
      ['male', 'female', 'prefer_not_to_say'],
      { errorMap: () => ({ message: t('signup.validation.genderRequired') }) }
    ),
    address: z
      .string()
      .trim()
      .min(10, t('signup.validation.addressTooShort'))
      .max(500, t('signup.validation.addressTooLong')),
    chronicDiseases: stringArray,
    drugAllergies: stringArray,
    foodAllergies: stringArray,
  });
}

/**
 * Convert a user object (from Redux / backend) into form-ready
 * defaultValues. The two shapes differ in one place: `age` is a number
 * in the user object but a string in the form. Pass the result of this
 * function to `useForm({ defaultValues })`.
 *
 * Missing fields are filled with safe defaults so the form mounts cleanly
 * even on partial data.
 *
 * @param {object | null} user
 * @returns {object} defaults
 */
export function userToFormDefaults(user) {
  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    age: user?.age != null ? String(user.age) : '',
    gender: user?.gender ?? '',
    address: user?.address ?? '',
    chronicDiseases: user?.chronicDiseases ?? [],
    drugAllergies: user?.drugAllergies ?? [],
    foodAllergies: user?.foodAllergies ?? [],
  };
}
